import type { ProjectBudgetPeriodInput, ProjectBudgetStatus } from './api'

export function budgetLevel(usedPct: number | null): 'ok' | 'warn' | 'over' {
  if (usedPct != null && usedPct >= 100) return 'over'
  if (usedPct != null && usedPct >= 80) return 'warn'
  return 'ok'
}

const CLASSES = {
  ok: { text: 'text-text-tertiary tabular-nums', bar: 'bg-accent', value: 'text-text-primary' },
  warn: { text: 'text-warning font-medium tabular-nums', bar: 'bg-warning', value: 'text-warning' },
  over: { text: 'text-danger font-medium tabular-nums', bar: 'bg-danger', value: 'text-danger' },
}

export function budgetClasses(usedPct: number | null) {
  return CLASSES[budgetLevel(usedPct)]
}

export function formatHours(hours: number | null): string {
  return hours == null ? '—' : `${hours.toLocaleString('de-DE', { maximumFractionDigits: 2 })} h`
}

/**
 * True when the status carries contingent figures. Gates rendering on the
 * data itself rather than on `period`, so a status with numbers but without
 * a period object is never hidden behind "Kein Kontingent".
 */
export function hasBudgetData(
  status: Pick<ProjectBudgetStatus, 'contingentHours'> | null | undefined,
): status is ProjectBudgetStatus {
  return status?.contingentHours != null
}

/**
 * Default project for the calendar strip and the dashboard widget: the
 * project of the user's most recent booking when it has an active
 * contingent, otherwise the first project (listProjects order) with one.
 * A project without an active contingent is never preselected — the views
 * would only show "Kein Kontingent" for it.
 */
export function pickDefaultProjectId(
  lastProjectId: string | null | undefined,
  statusByProject: Record<string, Pick<ProjectBudgetStatus, 'contingentHours'> | undefined>,
): string | null {
  if (lastProjectId && hasBudgetData(statusByProject[lastProjectId])) return lastProjectId
  return Object.keys(statusByProject).find((id) => hasBudgetData(statusByProject[id])) ?? null
}

export function validateBudgetPeriod(input: ProjectBudgetPeriodInput): string | null {
  if (!input.fromDate || !input.toDate) return 'Bitte Von und Bis angeben.'
  // Both values come from <input type="date"> and are zero-padded ISO dates
  // (YYYY-MM-DD), so lexical order equals chronological order.
  if (input.fromDate > input.toDate) return 'Von muss vor oder am Bis-Datum liegen.'
  if (!Number.isFinite(input.hours) || input.hours <= 0) return 'Stunden müssen größer als 0 sein.'
  return null
}
